import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import PlayerShotProfileCard from '../../components/basketball/PlayerShotProfileCard';
import FreeThrowTab from '../../components/basketball/free-throw/FreeThrowTab';
import AgentTracePanel from '../../components/shared/AgentTracePanel';

// Configure marked for chat rendering
marked.setOptions({ breaks: true, gfm: true });

const API_BASE = process.env.REACT_APP_API_URL || '';
const GCS_PREFIX = 'analysis/cs3s-biomech/v2';
const TOTAL_EXPECTED_BATCHES = 106;

// Dataset classification
const DATASET_A_LABEL = 'Pre-Christmas (Dataset A)';
const DATASET_B_LABEL = 'Post-Christmas (Dataset B)';

const STAT_BASELINE = {
  A: { uncontested3pt: 36.7, cs3pt: 37.4, rank3pt: '7th', rankCS: '12th', games: 30 },
  B: { uncontested3pt: 32.3, cs3pt: 33.6, rank3pt: '30th', rankCS: '28th', games: 24 },
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const containerStyle = {
  minHeight: '100vh',
  background: '#000',
  color: '#e0e0e0',
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
};

const headerStyle = {
  background: '#000',
  borderBottom: '1px solid rgba(255,255,255,0.08)',
  padding: '20px 32px',
};

const cardStyle = {
  background: 'transparent',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: 12,
  padding: 20,
};

const statBoxStyle = (color) => ({
  ...cardStyle,
  borderLeft: `3px solid ${color}`,
  textAlign: 'center',
  minWidth: 140,
});

const progressBarBg = {
  width: '100%',
  height: 24,
  background: 'rgba(255,255,255,0.03)',
  borderRadius: 12,
  overflow: 'hidden',
  position: 'relative',
};

const badgeStyle = (color) => ({
  display: 'inline-block',
  padding: '2px 10px',
  borderRadius: 6,
  fontSize: 11,
  fontWeight: 600,
  background: `${color}22`,
  color: color,
  border: `1px solid ${color}44`,
});

const tabStyle = (active) => ({
  padding: '8px 20px',
  border: 'none',
  borderRadius: '8px 8px 0 0',
  cursor: 'pointer',
  fontWeight: active ? 700 : 400,
  fontSize: 13,
  color: active ? '#fff' : '#888',
  background: active ? 'rgba(99, 102, 241, 0.2)' : 'transparent',
  borderBottom: active ? '2px solid #6366f1' : '2px solid transparent',
  transition: 'all 0.2s',
});

// ─── Helpers ─────────────────────────────────────────────────────────────────

function avg(arr) {
  if (!arr || arr.length === 0) return null;
  return arr.reduce((a, b) => a + b, 0) / arr.length;
}

function pct(n, total) {
  if (!total) return '0';
  return ((n / total) * 100).toFixed(1);
}

function deltaStr(a, b) {
  if (a == null || b == null) return 'N/A';
  const d = b - a;
  const sign = d > 0 ? '+' : '';
  return `${sign}${d.toFixed(1)}`;
}

// ─── Progress Panel ──────────────────────────────────────────────────────────

function ProgressPanel({ batchFiles, totalExpected, synthesis }) {
  const completed = batchFiles.length;
  const pctDone = Math.min((completed / totalExpected) * 100, 100);
  const isComplete = synthesis != null;

  return (
    <div style={cardStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 16, color: '#fff' }}>Analysis Progress</h3>
        <span style={badgeStyle(isComplete ? '#22c55e' : '#f59e0b')}>
          {isComplete ? 'COMPLETE' : 'RUNNING'}
        </span>
      </div>
      <div style={progressBarBg}>
        <div style={{
          width: `${pctDone}%`,
          height: '100%',
          background: isComplete
            ? 'linear-gradient(90deg, #22c55e, #16a34a)'
            : 'linear-gradient(90deg, #6366f1, #8b5cf6)',
          borderRadius: 12,
          transition: 'width 0.5s ease',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 12,
          fontWeight: 700,
          color: '#fff',
        }}>
          {pctDone >= 10 ? `${pctDone.toFixed(0)}%` : ''}
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: 12, color: '#888' }}>
        <span>{completed} / {totalExpected} batches processed</span>
        <span>Model: gemini-3-flash-preview</span>
      </div>
      {synthesis?.metadata && (
        <div style={{ marginTop: 12, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, color: '#aaa' }}>
            Shots analyzed: <strong style={{ color: '#fff' }}>{synthesis.metadata.total_shots_analyzed}</strong>
          </span>
          <span style={{ fontSize: 12, color: '#aaa' }}>
            Dataset A: <strong style={{ color: '#60a5fa' }}>{synthesis.metadata.dataset_a_shots}</strong>
          </span>
          <span style={{ fontSize: 12, color: '#aaa' }}>
            Dataset B: <strong style={{ color: '#f87171' }}>{synthesis.metadata.dataset_b_shots}</strong>
          </span>
          <span style={{ fontSize: 12, color: '#aaa' }}>
            Completed: <strong style={{ color: '#22c55e' }}>{new Date(synthesis.metadata.analyzed_at).toLocaleString()}</strong>
          </span>
        </div>
      )}
    </div>
  );
}

// ─── Baseline Comparison ─────────────────────────────────────────────────────

function BaselineComparison() {
  const a = STAT_BASELINE.A;
  const b = STAT_BASELINE.B;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginTop: 16 }}>
      <div style={statBoxStyle('#60a5fa')}>
        <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>Uncontested 3PT%</div>
        <div style={{ fontSize: 24, fontWeight: 800, color: '#60a5fa' }}>{a.uncontested3pt}%</div>
        <div style={{ fontSize: 11, color: '#888' }}>{a.rank3pt} NBA</div>
        <div style={{ fontSize: 13, color: '#f87171', marginTop: 4 }}>{b.uncontested3pt}% ({b.rank3pt})</div>
        <div style={{ fontSize: 11, color: '#f87171' }}>{deltaStr(a.uncontested3pt, b.uncontested3pt)}%</div>
      </div>
      <div style={statBoxStyle('#a78bfa')}>
        <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>C&S 3PT%</div>
        <div style={{ fontSize: 24, fontWeight: 800, color: '#a78bfa' }}>{a.cs3pt}%</div>
        <div style={{ fontSize: 11, color: '#888' }}>{a.rankCS} NBA</div>
        <div style={{ fontSize: 13, color: '#f87171', marginTop: 4 }}>{b.cs3pt}% ({b.rankCS})</div>
        <div style={{ fontSize: 11, color: '#f87171' }}>{deltaStr(a.cs3pt, b.cs3pt)}%</div>
      </div>
      <div style={statBoxStyle('#34d399')}>
        <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>Pre-Xmas Games</div>
        <div style={{ fontSize: 24, fontWeight: 800, color: '#34d399' }}>{a.games}</div>
        <div style={{ fontSize: 11, color: '#888' }}>Dataset A</div>
      </div>
      <div style={statBoxStyle('#fb923c')}>
        <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>Post-Xmas Games</div>
        <div style={{ fontSize: 24, fontWeight: 800, color: '#fb923c' }}>{b.games}</div>
        <div style={{ fontSize: 11, color: '#888' }}>Dataset B</div>
      </div>
    </div>
  );
}

// ─── Macro Diagnosis Panel ───────────────────────────────────────────────────

function MacroDiagnosis({ synthesis }) {
  if (!synthesis?.Macro_Diagnosis) return null;
  return (
    <div style={{ ...cardStyle, borderLeft: '3px solid #ef4444', marginTop: 16 }}>
      <h3 style={{ margin: '0 0 12px', fontSize: 16, color: '#ef4444' }}>Macro Diagnosis</h3>
      <p style={{ margin: 0, fontSize: 14, lineHeight: 1.7, color: '#d1d5db' }}>
        {synthesis.Macro_Diagnosis}
      </p>
    </div>
  );
}

// ─── Dataset Comparison Panel ────────────────────────────────────────────────

function DatasetComparisonPanel({ synthesis }) {
  const comp = synthesis?.Dataset_Comparison;
  if (!comp) return null;

  const metrics = [
    { label: 'Avg C2R (s)', key: 'avg_c2r_ms', format: v => `${(v / 1000).toFixed(2)}s`, better: 'lower' },
    { label: 'CoM Loaded %', key: 'pct_com_loaded', format: v => `${v}%`, better: 'higher' },
    { label: 'Hand Ready %', key: 'pct_hand_ready', format: v => `${v}%`, better: 'higher' },
    { label: 'Quiet Eye %', key: 'pct_quiet_eye', format: v => `${v}%`, better: 'higher' },
    { label: 'Pelvic Fluid %', key: 'pct_pelvic_fluid', format: v => `${v}%`, better: 'higher' },
    { label: 'Processing Freezes', key: 'processing_freezes', format: v => v, better: 'lower' },
  ];

  return (
    <div style={{ ...cardStyle, marginTop: 16 }}>
      <h3 style={{ margin: '0 0 16px', fontSize: 16, color: '#fff' }}>Dataset A vs B — Biomechanical Comparison</h3>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
              <th style={{ textAlign: 'left', padding: '8px 12px', color: '#888' }}>Metric</th>
              <th style={{ textAlign: 'center', padding: '8px 12px', color: '#60a5fa' }}>Dataset A (Pre-Xmas)</th>
              <th style={{ textAlign: 'center', padding: '8px 12px', color: '#f87171' }}>Dataset B (Post-Xmas)</th>
              <th style={{ textAlign: 'center', padding: '8px 12px', color: '#888' }}>Delta</th>
            </tr>
          </thead>
          <tbody>
            {metrics.map(m => {
              const aVal = comp.dataset_a?.[m.key];
              const bVal = comp.dataset_b?.[m.key];
              const delta = (aVal != null && bVal != null) ? bVal - aVal : null;
              const isWorse = m.better === 'higher' ? delta < 0 : delta > 0;
              return (
                <tr key={m.key} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                  <td style={{ padding: '8px 12px', fontWeight: 500 }}>{m.label}</td>
                  <td style={{ padding: '8px 12px', textAlign: 'center', color: '#60a5fa' }}>
                    {aVal != null ? m.format(aVal) : 'N/A'}
                  </td>
                  <td style={{ padding: '8px 12px', textAlign: 'center', color: '#f87171' }}>
                    {bVal != null ? m.format(bVal) : 'N/A'}
                  </td>
                  <td style={{ padding: '8px 12px', textAlign: 'center', color: isWorse ? '#ef4444' : '#22c55e', fontWeight: 600 }}>
                    {delta != null ? `${delta > 0 ? '+' : ''}${typeof aVal === 'number' && aVal % 1 !== 0 ? delta.toFixed(1) : Math.round(delta)}` : 'N/A'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Player Matrix ───────────────────────────────────────────────────────────

function PlayerMatrix({ synthesis }) {
  const matrix = synthesis?.Player_Matrix;
  if (!matrix || matrix.length === 0) return null;

  return (
    <div style={{ ...cardStyle, marginTop: 16 }}>
      <h3 style={{ margin: '0 0 16px', fontSize: 16, color: '#fff' }}>Player Regression Matrix</h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {matrix.map((p, i) => (
          <div key={i} style={{
            ...cardStyle,
            background: 'transparent',
            borderLeft: `3px solid ${i === 0 ? '#ef4444' : i < 3 ? '#f59e0b' : '#6b7280'}`,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{
                  width: 28, height: 28, borderRadius: '50%',
                  background: i === 0 ? '#ef4444' : i < 3 ? '#f59e0b' : '#6b7280',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 13, fontWeight: 800, color: '#fff',
                }}>
                  {p.rank || i + 1}
                </span>
                <span style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>{p.player}</span>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <span style={{ fontSize: 11, color: '#888' }}>A: {p.dataset_a_shots} shots</span>
                <span style={{ fontSize: 11, color: '#888' }}>B: {p.dataset_b_shots} shots</span>
                {p.c2r_delta_ms && (
                  <span style={badgeStyle('#ef4444')}>C2R: {p.c2r_delta_ms}</span>
                )}
              </div>
            </div>
            <div style={{ fontSize: 13, color: '#f87171', fontWeight: 500, marginBottom: 4 }}>
              {p.primary_change || p.primary_failure_point}
            </div>
            {(p.secondary_change || p.secondary_failure_point) && (
              <div style={{ fontSize: 12, color: '#fb923c', marginBottom: 4 }}>
                {p.secondary_change || p.secondary_failure_point}
              </div>
            )}
            <p style={{ margin: '8px 0 0', fontSize: 12, lineHeight: 1.6, color: '#9ca3af' }}>
              {p.diagnosis}
            </p>
            {(p.cognitive_change || p.cognitive_regression || p.footwork_shift) && (
              <div style={{ display: 'flex', gap: 12, marginTop: 8, flexWrap: 'wrap' }}>
                {(p.cognitive_change || p.cognitive_regression) && (
                  <div style={{ fontSize: 11, color: '#c084fc' }}>
                    <strong>Cognitive:</strong> {p.cognitive_change || p.cognitive_regression}
                  </div>
                )}
                {p.footwork_shift && (
                  <div style={{ fontSize: 11, color: '#34d399' }}>
                    <strong>Footwork:</strong> {p.footwork_shift}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Evidence Chain ──────────────────────────────────────────────────────────

function EvidenceChain({ synthesis }) {
  const chain = synthesis?.Evidence_Chain;
  if (!chain || chain.length === 0) return null;

  return (
    <div style={{ ...cardStyle, marginTop: 16 }}>
      <h3 style={{ margin: '0 0 16px', fontSize: 16, color: '#fff' }}>Evidence Chain — Key Clips</h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {chain.map((e, i) => (
          <div key={i} style={{
            ...cardStyle,
            background: 'transparent',
            display: 'flex',
            gap: 16,
            alignItems: 'flex-start',
          }}>
            <span style={{
              minWidth: 28, height: 28, borderRadius: '50%',
              background: '#6366f1',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 13, fontWeight: 800, color: '#fff',
            }}>
              {e.rank || i + 1}
            </span>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                <span style={badgeStyle(e.dataset === 'A' ? '#60a5fa' : '#f87171')}>
                  {e.dataset === 'A' ? 'Pre-Xmas' : 'Post-Xmas'}
                </span>
                <span style={{ fontSize: 12, color: '#888' }}>{e.shooter}</span>
                <span style={{ fontSize: 12, color: '#666' }}>
                  {e.video_file} @ {e.timestamp_start}s-{e.timestamp_end}s
                </span>
              </div>
              <p style={{ margin: '4px 0 0', fontSize: 12, lineHeight: 1.5, color: '#9ca3af' }}>
                {e.description}
              </p>
              {e.modules_demonstrated && (
                <div style={{ display: 'flex', gap: 4, marginTop: 6 }}>
                  {e.modules_demonstrated.map((m, j) => (
                    <span key={j} style={badgeStyle('#8b5cf6')}>{m}</span>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Video Modal ─────────────────────────────────────────────────────────────

const GCS_VIDEO_PREFIX = 'segments/wch_cs3s-2025-2026-pre-all-star';

function VideoModal({ shot, onClose }) {
  const videoRef = useRef(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [loadingVideo, setLoadingVideo] = useState(true);
  const [videoError, setVideoError] = useState(null);
  const [clipProgress, setClipProgress] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [phase, setPhase] = useState('catch'); // catch → load → release → follow-through

  const ts = shot?.timestamp || {};
  const startSec = ts.start || shot?.start || 0;
  const endSec = ts.end || shot?.end || 0;
  const clipDuration = endSec - startSec;
  const m1 = shot?.module_1_kinematics || {};
  const m2 = shot?.module_2_footwork || {};
  const m3 = shot?.module_3_c2r || {};
  const m4 = shot?.module_4_cognitive || {};
  const c2rSec = m3.total_c2r_ms ? m3.total_c2r_ms / 1000 : null;

  useEffect(() => {
    if (!shot) return;
    let cancelled = false;
    setLoadingVideo(true);
    setVideoError(null);

    const gcsPath = `${GCS_VIDEO_PREFIX}/${shot.video_file}.mp4`;
    fetch(`${API_BASE}/api/gcs-signed-url?path=${encodeURIComponent(gcsPath)}`)
      .then(res => res.json())
      .then(data => {
        if (cancelled) return;
        const url = data.data?.url || data.url;
        if (url) {
          setVideoUrl(url);
        } else {
          setVideoError('Failed to get video URL');
        }
        setLoadingVideo(false);
      })
      .catch(err => {
        if (!cancelled) {
          setVideoError(err.message);
          setLoadingVideo(false);
        }
      });

    return () => { cancelled = true; };
  }, [shot]);

  // Seek to clip start + loop within clip bounds + track progress
  useEffect(() => {
    if (!videoRef.current || !videoUrl || !shot) return;
    const v = videoRef.current;
    const clipStart = Math.max(0, startSec - 0.5);
    const clipEnd = endSec + 0.5;

    const handleCanPlay = () => {
      v.currentTime = clipStart;
      v.play().catch(() => {});
    };

    const handleTimeUpdate = () => {
      const t = v.currentTime;
      // Loop back to start when past end
      if (t >= clipEnd) {
        v.currentTime = clipStart;
      }
      // Track progress within clip
      const elapsed = Math.max(0, t - clipStart);
      const total = clipEnd - clipStart;
      setClipProgress(total > 0 ? Math.min(1, elapsed / total) : 0);

      // Determine shot phase based on position in clip
      const relTime = t - startSec;
      if (c2rSec) {
        if (relTime < 0) setPhase('approach');
        else if (relTime < c2rSec * 0.4) setPhase('catch');
        else if (relTime < c2rSec * 0.8) setPhase('load');
        else if (relTime < c2rSec) setPhase('release');
        else setPhase('follow-through');
      } else {
        const pct = clipDuration > 0 ? relTime / clipDuration : 0;
        if (pct < 0) setPhase('approach');
        else if (pct < 0.25) setPhase('catch');
        else if (pct < 0.5) setPhase('load');
        else if (pct < 0.75) setPhase('release');
        else setPhase('follow-through');
      }
    };

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);

    v.addEventListener('canplay', handleCanPlay, { once: true });
    v.addEventListener('timeupdate', handleTimeUpdate);
    v.addEventListener('play', handlePlay);
    v.addEventListener('pause', handlePause);
    return () => {
      v.removeEventListener('timeupdate', handleTimeUpdate);
      v.removeEventListener('play', handlePlay);
      v.removeEventListener('pause', handlePause);
    };
  }, [videoUrl, shot, startSec, endSec, c2rSec, clipDuration]);

  if (!shot) return null;

  const gazeColor = m4.gaze_state === 'Quiet_Eye_Locked' ? '#22c55e' : '#ef4444';
  const gazeLabel = m4.gaze_state === 'Quiet_Eye_Locked' ? 'QUIET EYE' : 'SCANNING';
  const resultColor = shot.shot_result === 'Made' ? '#22c55e' : shot.shot_result === 'Missed' ? '#ef4444' : '#888';
  const handsColor = m1.hand_readiness === 'Ready' ? '#22c55e' : '#ef4444';
  const loadColor = m1.com_load === 'Loaded' ? '#22c55e' : '#ef4444';

  const phaseLabels = { approach: 'APPROACH', catch: 'CATCH', load: 'LOAD', release: 'RELEASE', 'follow-through': 'FOLLOW-THRU' };
  const phaseColors = { approach: '#888', catch: '#60a5fa', load: '#f59e0b', release: '#c084fc', 'follow-through': '#22c55e' };

  const handleReplay = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = Math.max(0, startSec - 0.5);
      videoRef.current.play().catch(() => {});
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }} onClick={onClose}>
      <div style={{
        background: '#0a0a0a', border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: 16, maxWidth: 960, width: '95vw', maxHeight: '90vh',
        overflow: 'auto',
      }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <div>
            <h3 style={{ margin: 0, color: '#fff', fontSize: 16 }}>
              {shot.shooter} — Shot #{shot.global_index}
            </h3>
            <p style={{ margin: '4px 0 0', fontSize: 12, color: '#888' }}>
              {shot.video_file} | {startSec.toFixed(1)}s - {endSec.toFixed(1)}s | Defender: {shot.defender} ({shot.defender_distance_ft}ft)
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button onClick={handleReplay} style={{
              background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)',
              color: '#fff', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontSize: 12,
            }}>Replay</button>
            <button onClick={onClose} style={{
              background: 'transparent', border: '1px solid rgba(255,255,255,0.15)',
              color: '#fff', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontSize: 13,
            }}>Close</button>
          </div>
        </div>

        {/* Video with HUD overlay */}
        <div style={{ padding: '0 20px', marginTop: 12 }}>
          {loadingVideo ? (
            <div style={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#888' }}>
              Loading video...
            </div>
          ) : videoError ? (
            <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#f87171' }}>
              {videoError}
            </div>
          ) : (
            <div style={{ position: 'relative', borderRadius: 8, overflow: 'hidden' }}>
              <video
                ref={videoRef}
                src={videoUrl}
                controls
                style={{ width: '100%', display: 'block', maxHeight: 480, background: '#000' }}
              />
              {/* === HUD OVERLAY === */}
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, pointerEvents: 'none' }}>
                {/* Top bar: Result + Gaze + Phase */}
                <div style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                  padding: '10px 14px',
                  background: 'linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, transparent 100%)',
                }}>
                  {/* Left: Result badge */}
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    {shot.shot_result && (
                      <div style={{
                        background: `${resultColor}22`, border: `1px solid ${resultColor}66`,
                        borderRadius: 6, padding: '3px 10px', fontSize: 13, fontWeight: 700, color: resultColor,
                        letterSpacing: 0.5,
                      }}>
                        {shot.shot_result === 'Made' ? 'MAKE' : shot.shot_result === 'Missed' ? 'MISS' : shot.shot_result}
                      </div>
                    )}
                    {shot.miss_type && shot.miss_type !== 'N/A' && (
                      <div style={{
                        background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.4)',
                        borderRadius: 6, padding: '3px 8px', fontSize: 11, color: '#f59e0b', fontWeight: 600,
                      }}>
                        {shot.miss_type}
                      </div>
                    )}
                  </div>

                  {/* Right: Phase indicator */}
                  <div style={{
                    background: `${phaseColors[phase]}22`, border: `1px solid ${phaseColors[phase]}55`,
                    borderRadius: 6, padding: '3px 10px', fontSize: 11, fontWeight: 700,
                    color: phaseColors[phase], letterSpacing: 1, textTransform: 'uppercase',
                  }}>
                    {phaseLabels[phase]}
                  </div>
                </div>
              </div>

              {/* Bottom HUD: telemetry strip */}
              <div style={{
                position: 'absolute', bottom: 44, left: 0, right: 0, pointerEvents: 'none',
                background: 'linear-gradient(to top, rgba(0,0,0,0.75) 0%, transparent 100%)',
                padding: '20px 14px 10px',
              }}>
                <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
                  {/* Gaze */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{
                      width: 8, height: 8, borderRadius: '50%', background: gazeColor,
                      boxShadow: `0 0 6px ${gazeColor}`,
                      animation: m4.gaze_state === 'Quiet_Eye_Locked' ? 'none' : 'pulse 1s infinite',
                    }} />
                    <span style={{ fontSize: 11, fontWeight: 700, color: gazeColor, letterSpacing: 0.8 }}>{gazeLabel}</span>
                  </div>
                  {/* C2R */}
                  {c2rSec && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span style={{ fontSize: 10, color: '#888', fontWeight: 600 }}>C2R</span>
                      <span style={{ fontSize: 12, fontWeight: 700, color: '#c084fc' }}>{c2rSec.toFixed(2)}s</span>
                    </div>
                  )}
                  {/* Knee */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ fontSize: 10, color: '#888', fontWeight: 600 }}>KNEE</span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: loadColor }}>
                      {m1.knee_flexion_estimate_deg ? `${m1.knee_flexion_estimate_deg}°` : (m1.com_load || '?')}
                    </span>
                  </div>
                  {/* Hands */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ fontSize: 10, color: '#888', fontWeight: 600 }}>HANDS</span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: handsColor }}>{m1.hand_readiness || '?'}</span>
                  </div>
                  {/* Footwork */}
                  {m2.kinetic_type && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span style={{ fontSize: 10, color: '#888', fontWeight: 600 }}>FOOT</span>
                      <span style={{ fontSize: 12, fontWeight: 700, color: '#60a5fa' }}>{m2.kinetic_type.replace(/_/g, ' ')}</span>
                    </div>
                  )}
                  {/* Defender distance */}
                  {shot.defender_distance_ft && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span style={{ fontSize: 10, color: '#888', fontWeight: 600 }}>CONTEST</span>
                      <span style={{ fontSize: 12, fontWeight: 700, color: shot.defender_distance_ft < 4 ? '#ef4444' : shot.defender_distance_ft < 6 ? '#f59e0b' : '#22c55e' }}>
                        {Number(shot.defender_distance_ft).toFixed(1)}ft
                      </span>
                    </div>
                  )}
                </div>
                {/* Clip progress bar */}
                <div style={{ marginTop: 8, height: 3, background: 'rgba(255,255,255,0.1)', borderRadius: 2, overflow: 'hidden' }}>
                  <div style={{
                    height: '100%', borderRadius: 2,
                    width: `${clipProgress * 100}%`,
                    background: `linear-gradient(90deg, #60a5fa, ${phaseColors[phase]})`,
                    transition: 'width 0.1s linear',
                  }} />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Notes */}
        {(m1.notes || m2.notes || m3.notes || m4.notes) && (
          <div style={{ padding: '12px 20px 16px' }}>
            <div style={{ fontSize: 11, color: '#888', marginBottom: 6, textTransform: 'uppercase' }}>Analysis Notes</div>
            <div style={{ fontSize: 12, color: '#aaa', lineHeight: 1.6 }}>
              {[m1.notes, m2.notes, m3.notes, m4.notes].filter(Boolean).join(' | ')}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Shot-Level Browser ──────────────────────────────────────────────────────

function ShotBrowser({ shots }) {
  const [filter, setFilter] = useState('all');
  const [playerFilter, setPlayerFilter] = useState('all');
  const [outcomeFilter, setOutcomeFilter] = useState('all');
  const [selectedShot, setSelectedShot] = useState(null);
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  if (!shots || shots.length === 0) return null;

  const players = [...new Set(shots.map(s => s.shooter))].sort();
  const hasOutcomeData = shots.some(s => s.shot_result && s.shot_result !== 'Unknown');
  let filtered = shots;
  if (filter !== 'all') filtered = filtered.filter(s => s.dataset === filter);
  if (playerFilter !== 'all') filtered = filtered.filter(s => s.shooter === playerFilter);
  if (outcomeFilter !== 'all') filtered = filtered.filter(s => s.shot_result === outcomeFilter);

  // Sorting
  const handleSort = (col) => {
    if (sortCol === col) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortCol(col);
      setSortDir('asc');
    }
  };

  const sortArrow = (col) => sortCol === col ? (sortDir === 'asc' ? ' \u25B2' : ' \u25BC') : '';

  const getSortValue = (s, col) => {
    const m1 = s.module_1_kinematics || {};
    const m2 = s.module_2_footwork || {};
    const m3 = s.module_3_c2r || {};
    const m4 = s.module_4_cognitive || {};
    switch (col) {
      case 'index': return s.global_index || 0;
      case 'dataset': return s.dataset || '';
      case 'shooter': return s.shooter || '';
      case 'result': return s.shot_result || '';
      case 'miss_type': return s.miss_type || '';
      case 'com': return m1.knee_flexion_estimate_deg || 0;
      case 'hands': return m1.hand_readiness || '';
      case 'pelvis': return m1.pelvic_alignment || '';
      case 'footwork': return m2.kinetic_type || '';
      case 'cadence': return m2.cadence_flag || '';
      case 'c2r': return m3.total_c2r_ms || 0;
      case 'gaze': return m4.gaze_state || '';
      case 'freeze': return m4.processing_freeze ? 1 : 0;
      default: return '';
    }
  };

  if (sortCol) {
    filtered = [...filtered].sort((a, b) => {
      const va = getSortValue(a, sortCol);
      const vb = getSortValue(b, sortCol);
      const cmp = typeof va === 'number' ? va - vb : String(va).localeCompare(String(vb));
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }

  return (
    <div style={{ ...cardStyle, marginTop: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <h3 style={{ margin: 0, fontSize: 16, color: '#fff' }}>Shot-Level Telemetry ({filtered.length} shots)</h3>
        <div style={{ display: 'flex', gap: 8 }}>
          <select
            value={filter}
            onChange={e => setFilter(e.target.value)}
            style={{ background: 'rgba(255,255,255,0.03)', color: '#fff', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 6, padding: '4px 8px', fontSize: 12 }}
          >
            <option value="all">All Datasets</option>
            <option value="A">Dataset A (Pre-Xmas)</option>
            <option value="B">Dataset B (Post-Xmas)</option>
          </select>
          <select
            value={playerFilter}
            onChange={e => setPlayerFilter(e.target.value)}
            style={{ background: 'rgba(255,255,255,0.03)', color: '#fff', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 6, padding: '4px 8px', fontSize: 12 }}
          >
            <option value="all">All Players</option>
            {players.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          {hasOutcomeData && (
            <select
              value={outcomeFilter}
              onChange={e => setOutcomeFilter(e.target.value)}
              style={{ background: 'rgba(255,255,255,0.03)', color: '#fff', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 6, padding: '4px 8px', fontSize: 12 }}
            >
              <option value="all">All Outcomes</option>
              <option value="Made">Made</option>
              <option value="Missed">Missed</option>
            </select>
          )}
        </div>
      </div>
      <div style={{ overflowX: 'auto', maxHeight: 500, overflowY: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 1150 }}>
          <thead style={{ position: 'sticky', top: 0, background: '#000', zIndex: 1 }}>
            <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
              <th style={{ textAlign: 'center', padding: '6px 4px', color: '#888', width: 32 }}></th>
              {[
                { col: 'index', label: '#', align: 'left' },
                { col: 'dataset', label: 'Set', align: 'left' },
                { col: 'shooter', label: 'Shooter', align: 'left' },
                { col: 'result', label: 'Result', align: 'center' },
                { col: 'miss_type', label: 'Miss Type', align: 'center' },
                { col: 'com', label: 'Knee Flex', align: 'center' },
                { col: 'hands', label: 'Hands', align: 'center' },
                { col: 'pelvis', label: 'Pelvis', align: 'center' },
                { col: 'footwork', label: 'Footwork', align: 'center' },
                { col: 'cadence', label: 'Cadence', align: 'center' },
                { col: 'c2r', label: 'C2R (s)', align: 'center' },
                { col: 'gaze', label: 'Gaze', align: 'center' },
                { col: 'freeze', label: 'Freeze', align: 'center' },
              ].map(h => (
                <th
                  key={h.col}
                  onClick={() => handleSort(h.col)}
                  style={{ textAlign: h.align, padding: '6px 8px', color: sortCol === h.col ? '#fff' : '#888', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                >
                  {h.label}{sortArrow(h.col)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.slice(0, 200).map((s, i) => {
              const m1 = s.module_1_kinematics || {};
              const m2 = s.module_2_footwork || {};
              const m3 = s.module_3_c2r || {};
              const m4 = s.module_4_cognitive || {};
              return (
                <tr
                  key={i}
                  style={{ borderBottom: '1px solid rgba(255,255,255,0.03)', cursor: 'pointer' }}
                  onClick={() => setSelectedShot(s)}
                  title="Click to view video"
                >
                  <td style={{ padding: '5px 4px', textAlign: 'center' }}>
                    <span style={{ fontSize: 14, cursor: 'pointer', opacity: 0.5 }} title="Play video clip">&#9654;</span>
                  </td>
                  <td style={{ padding: '5px 8px', color: '#666' }}>{s.global_index || i + 1}</td>
                  <td style={{ padding: '5px 8px' }}>
                    <span style={badgeStyle(s.dataset === 'A' ? '#60a5fa' : '#f87171')}>
                      {s.dataset}
                    </span>
                  </td>
                  <td style={{ padding: '5px 8px', color: '#fff', fontWeight: 500 }}>{s.shooter}</td>
                  <td style={{ padding: '5px 8px', textAlign: 'center' }}>
                    {s.shot_result ? (
                      <span style={badgeStyle(
                        s.shot_result === 'Made' ? '#22c55e' :
                        s.shot_result === 'Missed' ? '#ef4444' : '#6b7280'
                      )}>
                        {s.shot_result}
                      </span>
                    ) : (
                      <span style={{ color: '#555' }}>-</span>
                    )}
                  </td>
                  <td style={{ padding: '5px 8px', textAlign: 'center' }}>
                    {s.miss_type && s.miss_type !== 'N/A' ? (
                      <span style={badgeStyle(
                        s.miss_type === 'Short' ? '#f59e0b' :
                        s.miss_type === 'Long' ? '#fb923c' :
                        s.miss_type === 'Left' ? '#a78bfa' :
                        s.miss_type === 'Right' ? '#c084fc' :
                        s.miss_type === 'Rim-In-Out' ? '#38bdf8' : '#6b7280'
                      )}>
                        {s.miss_type}
                      </span>
                    ) : (
                      <span style={{ color: '#555' }}>-</span>
                    )}
                  </td>
                  <td style={{ padding: '5px 8px', textAlign: 'center' }}>
                    <span style={badgeStyle(m1.com_load === 'Loaded' ? '#22c55e' : '#ef4444')}>
                      {m1.knee_flexion_estimate_deg ? `${m1.knee_flexion_estimate_deg}°` : (m1.com_load || '?')}
                    </span>
                  </td>
                  <td style={{ padding: '5px 8px', textAlign: 'center' }}>
                    <span style={badgeStyle(m1.hand_readiness === 'Ready' ? '#22c55e' : '#ef4444')}>
                      {m1.hand_readiness || '?'}
                    </span>
                  </td>
                  <td style={{ padding: '5px 8px', textAlign: 'center' }}>
                    <span style={badgeStyle(m1.pelvic_alignment === 'Fluid Square' ? '#22c55e' : '#ef4444')}>
                      {m1.pelvic_alignment === 'Fluid Square' ? 'Fluid' : 'Torsion'}
                    </span>
                  </td>
                  <td style={{ padding: '5px 8px', textAlign: 'center', color: '#ccc' }}>
                    {m2.kinetic_type || '?'}
                  </td>
                  <td style={{ padding: '5px 8px', textAlign: 'center' }}>
                    <span style={badgeStyle(
                      m2.cadence_flag === 'Normal' ? '#22c55e' :
                      m2.cadence_flag === 'Rushed' ? '#f59e0b' :
                      m2.cadence_flag === 'Delayed/Heavy' ? '#ef4444' : '#6b7280'
                    )}>
                      {m2.cadence_flag || 'N/A'}
                    </span>
                  </td>
                  <td style={{ padding: '5px 8px', textAlign: 'center', color: '#ccc', fontFamily: 'monospace' }}>
                    {m3.total_c2r_ms ? (m3.total_c2r_ms / 1000).toFixed(2) : '?'}
                  </td>
                  <td style={{ padding: '5px 8px', textAlign: 'center' }}>
                    <span style={badgeStyle(m4.gaze_state === 'Quiet_Eye_Locked' ? '#22c55e' : '#ef4444')}>
                      {m4.gaze_state === 'Quiet_Eye_Locked' ? 'Locked' : 'Scanning'}
                    </span>
                  </td>
                  <td style={{ padding: '5px 8px', textAlign: 'center' }}>
                    {m4.processing_freeze ? <span style={badgeStyle('#ef4444')}>YES</span> : <span style={{ color: '#555' }}>-</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {selectedShot && <VideoModal shot={selectedShot} onClose={() => setSelectedShot(null)} />}
    </div>
  );
}

// ─── Aggregate Stats (computed from shot data) ──────────────────────────────

function AggregateStats({ shots }) {
  if (!shots || shots.length === 0) return null;

  const byDataset = { A: [], B: [] };
  shots.forEach(s => { if (byDataset[s.dataset]) byDataset[s.dataset].push(s); });

  const computeStats = (arr) => {
    if (arr.length === 0) return null;
    const m1s = arr.map(s => s.module_1_kinematics).filter(Boolean);
    const m2s = arr.map(s => s.module_2_footwork).filter(Boolean);
    const m3s = arr.map(s => s.module_3_c2r).filter(Boolean);
    const m4s = arr.map(s => s.module_4_cognitive).filter(Boolean);
    return {
      count: arr.length,
      pctLoaded: pct(m1s.filter(m => m.com_load === 'Loaded').length, m1s.length),
      pctReady: pct(m1s.filter(m => m.hand_readiness === 'Ready').length, m1s.length),
      pctFluid: pct(m1s.filter(m => m.pelvic_alignment === 'Fluid Square').length, m1s.length),
      avgC2R: avg(m3s.map(m => m.total_c2r_ms).filter(Boolean)),
      pctQuietEye: pct(m4s.filter(m => m.gaze_state === 'Quiet_Eye_Locked').length, m4s.length),
      freezes: m4s.filter(m => m.processing_freeze).length,
      pct12Step: pct(m2s.filter(m => m.kinetic_type === '1-2_Step').length, m2s.length),
    };
  };

  const statsA = computeStats(byDataset.A);
  const statsB = computeStats(byDataset.B);

  if (!statsA || !statsB) return null;

  const rows = [
    { label: 'Shots Analyzed', a: statsA.count, b: statsB.count },
    { label: 'CoM Loaded %', a: `${statsA.pctLoaded}%`, b: `${statsB.pctLoaded}%`, better: 'higher', aNum: parseFloat(statsA.pctLoaded), bNum: parseFloat(statsB.pctLoaded) },
    { label: 'Hand Ready %', a: `${statsA.pctReady}%`, b: `${statsB.pctReady}%`, better: 'higher', aNum: parseFloat(statsA.pctReady), bNum: parseFloat(statsB.pctReady) },
    { label: 'Pelvic Fluid %', a: `${statsA.pctFluid}%`, b: `${statsB.pctFluid}%`, better: 'higher', aNum: parseFloat(statsA.pctFluid), bNum: parseFloat(statsB.pctFluid) },
    { label: 'Avg C2R (s)', a: statsA.avgC2R ? `${(statsA.avgC2R / 1000).toFixed(2)}s` : 'N/A', b: statsB.avgC2R ? `${(statsB.avgC2R / 1000).toFixed(2)}s` : 'N/A', better: 'lower', aNum: statsA.avgC2R, bNum: statsB.avgC2R },
    { label: 'Quiet Eye %', a: `${statsA.pctQuietEye}%`, b: `${statsB.pctQuietEye}%`, better: 'higher', aNum: parseFloat(statsA.pctQuietEye), bNum: parseFloat(statsB.pctQuietEye) },
    { label: 'Processing Freezes', a: statsA.freezes, b: statsB.freezes, better: 'lower', aNum: statsA.freezes, bNum: statsB.freezes },
    { label: '1-2 Step %', a: `${statsA.pct12Step}%`, b: `${statsB.pct12Step}%` },
  ];

  return (
    <div style={{ ...cardStyle, marginTop: 16 }}>
      <h3 style={{ margin: '0 0 16px', fontSize: 16, color: '#fff' }}>Computed Aggregate Comparison (from shot data)</h3>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
            <th style={{ textAlign: 'left', padding: '8px 12px', color: '#888' }}>Metric</th>
            <th style={{ textAlign: 'center', padding: '8px 12px', color: '#60a5fa' }}>Dataset A</th>
            <th style={{ textAlign: 'center', padding: '8px 12px', color: '#f87171' }}>Dataset B</th>
            <th style={{ textAlign: 'center', padding: '8px 12px', color: '#888' }}>Trend</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => {
            let trendColor = '#888';
            if (r.aNum != null && r.bNum != null && r.better) {
              const isWorse = r.better === 'higher' ? r.bNum < r.aNum : r.bNum > r.aNum;
              trendColor = isWorse ? '#ef4444' : '#22c55e';
            }
            return (
              <tr key={r.label} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                <td style={{ padding: '6px 12px', fontWeight: 500 }}>{r.label}</td>
                <td style={{ padding: '6px 12px', textAlign: 'center', color: '#60a5fa' }}>{r.a}</td>
                <td style={{ padding: '6px 12px', textAlign: 'center', color: '#f87171' }}>{r.b}</td>
                <td style={{ padding: '6px 12px', textAlign: 'center', color: trendColor, fontWeight: 600 }}>
                  {r.aNum != null && r.bNum != null && r.better
                    ? (r.better === 'higher' ? (r.bNum >= r.aNum ? 'OK' : 'REGRESSED') : (r.bNum <= r.aNum ? 'OK' : 'REGRESSED'))
                    : '-'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── Player Shot Profiles (Built from CS3s shot data) ────────────────────────

function buildPlayerProfiles(shots) {
  if (!shots || shots.length === 0) return [];

  const byPlayer = {};
  for (const s of shots) {
    if (!s.shooter) continue;
    if (!byPlayer[s.shooter]) byPlayer[s.shooter] = [];
    byPlayer[s.shooter].push(s);
  }

  return Object.entries(byPlayer).map(([name, playerShots]) => {
    // All CS3s shots are catch-and-shoot, so they're all CCS
    // Group by shot_type for category breakdown
    const byType = {};
    for (const s of playerShots) {
      const type = s.shot_type || 'Unknown';
      if (!byType[type]) byType[type] = { fga: 0, fgm: 0, c2r_times: [], prep_flags: new Set() };
      byType[type].fga++;
      if (s.shot_result === 'Made') byType[type].fgm++;
      const c2r = s.module_3_c2r?.total_c2r_ms;
      if (c2r) byType[type].c2r_times.push(c2r / 1000);
      const m1 = s.module_1_kinematics || {};
      if (m1.hand_readiness === 'Late Gather') byType[type].prep_flags.add('late gather');
      if (m1.pelvic_alignment === 'Compensatory Torsion') byType[type].prep_flags.add('compensatory torsion');
    }

    const categories = Object.entries(byType)
      .sort((a, b) => b[1].fga - a[1].fga)
      .slice(0, 5)
      .map(([label, data], i) => ({
        rank: i + 1,
        label,
        fga: data.fga,
        fgm: data.fgm,
        fg_pct: data.fga > 0 ? data.fgm / data.fga : null,
        catch_to_release_avg_sec: data.c2r_times.length > 0
          ? data.c2r_times.reduce((a, b) => a + b, 0) / data.c2r_times.length
          : null,
        preshot_prep_flags: [...data.prep_flags],
        footwork: null, // Would need footwork breakdown per type
        video_clip_ids: [],
      }));

    const totalMade = playerShots.filter(s => s.shot_result === 'Made').length;
    const totalMissed = playerShots.filter(s => s.shot_result === 'Missed').length;

    return {
      player: { name, avatar_url: null, team: 'San Antonio Spurs' },
      scs: { total_attempts: 0, categories: [] }, // No SCS in C&S dataset
      ccs: {
        total_attempts: playerShots.length,
        made: totalMade,
        missed: totalMissed,
        fg_pct: (totalMade + totalMissed) > 0 ? totalMade / (totalMade + totalMissed) : null,
        categories,
      },
    };
  }).sort((a, b) => b.ccs.total_attempts - a.ccs.total_attempts);
}

function PlayerProfileCards({ shots }) {
  const profiles = useMemo(() => buildPlayerProfiles(shots), [shots]);

  if (profiles.length === 0) {
    return (
      <div style={{ ...cardStyle, marginTop: 16, textAlign: 'center', color: '#888' }}>
        No shot data available for player profiles.
      </div>
    );
  }

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))', gap: 16 }}>
        {profiles.map(p => (
          <PlayerShotProfileCard key={p.player.name} playerData={p} />
        ))}
      </div>
    </div>
  );
}

// ─── Coaching Q&A — Answers the Original Prompt ─────────────────────────────

function CoachingQA({ shots, synthesis }) {
  if (!shots || shots.length === 0) return (
    <div style={{ ...cardStyle, marginTop: 16, textAlign: 'center', color: '#888' }}>
      Loading shot data...
    </div>
  );

  const byDataset = { A: [], B: [] };
  shots.forEach(s => { if (byDataset[s.dataset]) byDataset[s.dataset].push(s); });

  // ── Q1: Biomechanical Differences ──
  const computeBiomech = (arr) => {
    if (arr.length === 0) return null;
    const m1s = arr.map(s => s.module_1_kinematics).filter(Boolean);
    const m3s = arr.map(s => s.module_3_c2r).filter(Boolean);
    const m4s = arr.map(s => s.module_4_cognitive).filter(Boolean);
    return {
      count: arr.length,
      pctLoaded: m1s.length ? (m1s.filter(m => m.com_load === 'Loaded').length / m1s.length * 100).toFixed(1) : 'N/A',
      pctReady: m1s.length ? (m1s.filter(m => m.hand_readiness === 'Ready').length / m1s.length * 100).toFixed(1) : 'N/A',
      pctFluid: m1s.length ? (m1s.filter(m => m.pelvic_alignment === 'Fluid Square').length / m1s.length * 100).toFixed(1) : 'N/A',
      avgC2R: avg(m3s.map(m => m.total_c2r_ms).filter(Boolean)),
      pctQuietEye: m4s.length ? (m4s.filter(m => m.gaze_state === 'Quiet_Eye_Locked').length / m4s.length * 100).toFixed(1) : 'N/A',
      freezes: m4s.filter(m => m.processing_freeze).length,
    };
  };

  const statsA = computeBiomech(byDataset.A);
  const statsB = computeBiomech(byDataset.B);

  // ── Q2: Catch-to-Release Speed ──
  const c2rByPlayer = {};
  shots.forEach(s => {
    const c2r = s.module_3_c2r?.total_c2r_ms;
    if (!c2r || !s.shooter) return;
    if (!c2rByPlayer[s.shooter]) c2rByPlayer[s.shooter] = { A: [], B: [] };
    if (c2rByPlayer[s.shooter][s.dataset]) c2rByPlayer[s.shooter][s.dataset].push(c2r);
  });

  const playerC2R = Object.entries(c2rByPlayer)
    .map(([name, data]) => {
      const avgA = data.A.length ? data.A.reduce((a, b) => a + b, 0) / data.A.length : null;
      const avgB = data.B.length ? data.B.reduce((a, b) => a + b, 0) / data.B.length : null;
      const delta = (avgA && avgB) ? avgB - avgA : null;
      return { name, avgA, avgB, delta, totalShots: data.A.length + data.B.length };
    })
    .filter(p => p.avgA && p.avgB)
    .sort((a, b) => (b.delta || 0) - (a.delta || 0));

  // ── Q3: Player Woes (FG% drop) ──
  const playerFG = {};
  shots.forEach(s => {
    if (!s.shooter || !s.shot_result || s.shot_result === 'Unknown') return;
    if (!playerFG[s.shooter]) playerFG[s.shooter] = { A: { made: 0, total: 0 }, B: { made: 0, total: 0 } };
    if (playerFG[s.shooter][s.dataset]) {
      playerFG[s.shooter][s.dataset].total++;
      if (s.shot_result === 'Made') playerFG[s.shooter][s.dataset].made++;
    }
  });

  const playerWoes = Object.entries(playerFG)
    .map(([name, data]) => {
      const fgA = data.A.total > 0 ? (data.A.made / data.A.total * 100) : null;
      const fgB = data.B.total > 0 ? (data.B.made / data.B.total * 100) : null;
      const drop = (fgA !== null && fgB !== null) ? fgB - fgA : null;
      return { name, fgA, fgB, drop, shotsA: data.A.total, shotsB: data.B.total };
    })
    .filter(p => p.fgA !== null && p.fgB !== null && p.shotsA >= 5 && p.shotsB >= 5)
    .sort((a, b) => (a.drop || 0) - (b.drop || 0));

  // ── Q4: Footwork Success Rate ──
  const footworkStats = { '1-2_Step': { A: { made: 0, total: 0 }, B: { made: 0, total: 0 } }, 'Two_Foot_Hop': { A: { made: 0, total: 0 }, B: { made: 0, total: 0 } } };
  shots.forEach(s => {
    const fw = s.module_2_footwork?.kinetic_type;
    const result = s.shot_result;
    if (!fw || !result || result === 'Unknown') return;
    const key = fw === '1-2_Step' ? '1-2_Step' : 'Two_Foot_Hop';
    if (footworkStats[key][s.dataset]) {
      footworkStats[key][s.dataset].total++;
      if (result === 'Made') footworkStats[key][s.dataset].made++;
    }
  });

  const fwFG = (d) => d.total > 0 ? (d.made / d.total * 100).toFixed(1) : 'N/A';

  const sectionStyle = { ...cardStyle, marginTop: 16, borderLeft: '3px solid #6366f1' };
  const questionStyle = { fontSize: 18, fontWeight: 800, color: '#fff', margin: '0 0 12px' };
  const subheadStyle = { fontSize: 14, fontWeight: 600, color: '#a78bfa', margin: '16px 0 8px' };

  return (
    <div style={{ marginTop: 16 }}>
      {/* Q1: Biomechanical Differences */}
      <div style={sectionStyle}>
        <h3 style={questionStyle}>Are there biomechanical differences Pre vs Post Christmas?</h3>
        {statsA && statsB ? (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                <th style={{ textAlign: 'left', padding: '8px 12px', color: '#888' }}>Metric</th>
                <th style={{ textAlign: 'center', padding: '8px 12px', color: '#60a5fa' }}>Pre-Xmas ({statsA.count} shots)</th>
                <th style={{ textAlign: 'center', padding: '8px 12px', color: '#f87171' }}>Post-Xmas ({statsB.count} shots)</th>
                <th style={{ textAlign: 'center', padding: '8px 12px', color: '#888' }}>Delta</th>
              </tr>
            </thead>
            <tbody>
              {[
                { label: 'CoM Loaded %', a: statsA.pctLoaded, b: statsB.pctLoaded, better: 'higher' },
                { label: 'Hand Ready %', a: statsA.pctReady, b: statsB.pctReady, better: 'higher' },
                { label: 'Pelvic Fluid %', a: statsA.pctFluid, b: statsB.pctFluid, better: 'higher' },
                { label: 'Avg C2R (s)', a: statsA.avgC2R ? (statsA.avgC2R / 1000).toFixed(2) : 'N/A', b: statsB.avgC2R ? (statsB.avgC2R / 1000).toFixed(2) : 'N/A', better: 'lower', rawA: statsA.avgC2R, rawB: statsB.avgC2R },
                { label: 'Quiet Eye %', a: statsA.pctQuietEye, b: statsB.pctQuietEye, better: 'higher' },
                { label: 'Processing Freezes', a: statsA.freezes, b: statsB.freezes, better: 'lower' },
              ].map(row => {
                const numA = row.rawA ?? parseFloat(row.a);
                const numB = row.rawB ?? parseFloat(row.b);
                const delta = (!isNaN(numA) && !isNaN(numB)) ? numB - numA : null;
                const isWorse = delta !== null && row.better === 'higher' ? delta < 0 : delta > 0;
                return (
                  <tr key={row.label} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                    <td style={{ padding: '6px 12px', fontWeight: 500 }}>{row.label}</td>
                    <td style={{ padding: '6px 12px', textAlign: 'center', color: '#60a5fa' }}>{typeof row.a === 'number' ? row.a : row.a}{row.label.includes('%') && typeof row.a === 'string' ? '%' : ''}</td>
                    <td style={{ padding: '6px 12px', textAlign: 'center', color: '#f87171' }}>{typeof row.b === 'number' ? row.b : row.b}{row.label.includes('%') && typeof row.b === 'string' ? '%' : ''}</td>
                    <td style={{ padding: '6px 12px', textAlign: 'center', color: isWorse ? '#ef4444' : '#22c55e', fontWeight: 600 }}>
                      {delta !== null ? `${delta > 0 ? '+' : ''}${row.label.includes('C2R') ? (delta / 1000).toFixed(2) + 's' : typeof delta === 'number' && delta % 1 !== 0 ? delta.toFixed(1) : Math.round(delta)}` : '-'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : <p style={{ color: '#888' }}>Not enough data for comparison.</p>}
        {synthesis?.Macro_Diagnosis && (
          <div style={{ marginTop: 12, padding: 12, background: 'rgba(99, 102, 241, 0.08)', borderRadius: 8 }}>
            <div style={{ fontSize: 11, color: '#a78bfa', fontWeight: 600, marginBottom: 4 }}>AI MACRO DIAGNOSIS</div>
            <p style={{ margin: 0, fontSize: 13, lineHeight: 1.7, color: '#d1d5db' }}>{synthesis.Macro_Diagnosis}</p>
          </div>
        )}
      </div>

      {/* Q2: Catch-to-Release Speed */}
      <div style={{ ...sectionStyle, borderLeftColor: '#c084fc' }}>
        <h3 style={questionStyle}>Catch-to-Release Speed: Pre vs Post Christmas</h3>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
              <th style={{ textAlign: 'left', padding: '8px 12px', color: '#888' }}>Player</th>
              <th style={{ textAlign: 'center', padding: '8px 12px', color: '#60a5fa' }}>Pre-Xmas Avg C2R</th>
              <th style={{ textAlign: 'center', padding: '8px 12px', color: '#f87171' }}>Post-Xmas Avg C2R</th>
              <th style={{ textAlign: 'center', padding: '8px 12px', color: '#888' }}>Delta</th>
            </tr>
          </thead>
          <tbody>
            {playerC2R.map(p => (
              <tr key={p.name} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                <td style={{ padding: '6px 12px', fontWeight: 500, color: '#fff' }}>{p.name}</td>
                <td style={{ padding: '6px 12px', textAlign: 'center', color: '#60a5fa', fontFamily: 'monospace' }}>{(p.avgA / 1000).toFixed(2)}s</td>
                <td style={{ padding: '6px 12px', textAlign: 'center', color: '#f87171', fontFamily: 'monospace' }}>{(p.avgB / 1000).toFixed(2)}s</td>
                <td style={{ padding: '6px 12px', textAlign: 'center', fontWeight: 600, fontFamily: 'monospace', color: p.delta > 0 ? '#ef4444' : '#22c55e' }}>
                  {p.delta > 0 ? '+' : ''}{(p.delta / 1000).toFixed(2)}s
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Q3: Specific Player Woes */}
      <div style={{ ...sectionStyle, borderLeftColor: '#ef4444' }}>
        <h3 style={questionStyle}>Specific Player Woes (Biggest FG% Drops)</h3>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
              <th style={{ textAlign: 'left', padding: '8px 12px', color: '#888' }}>Player</th>
              <th style={{ textAlign: 'center', padding: '8px 12px', color: '#60a5fa' }}>Pre-Xmas FG%</th>
              <th style={{ textAlign: 'center', padding: '8px 12px', color: '#f87171' }}>Post-Xmas FG%</th>
              <th style={{ textAlign: 'center', padding: '8px 12px', color: '#888' }}>Drop</th>
              <th style={{ textAlign: 'center', padding: '8px 12px', color: '#888' }}>Shots (A/B)</th>
            </tr>
          </thead>
          <tbody>
            {playerWoes.map(p => (
              <tr key={p.name} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                <td style={{ padding: '6px 12px', fontWeight: 500, color: '#fff' }}>{p.name}</td>
                <td style={{ padding: '6px 12px', textAlign: 'center', color: '#60a5fa' }}>{p.fgA.toFixed(1)}%</td>
                <td style={{ padding: '6px 12px', textAlign: 'center', color: '#f87171' }}>{p.fgB.toFixed(1)}%</td>
                <td style={{ padding: '6px 12px', textAlign: 'center', fontWeight: 700, color: p.drop < 0 ? '#ef4444' : '#22c55e' }}>
                  {p.drop > 0 ? '+' : ''}{p.drop.toFixed(1)}%
                </td>
                <td style={{ padding: '6px 12px', textAlign: 'center', color: '#888' }}>{p.shotsA}/{p.shotsB}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Q4: Footwork Success Rate */}
      <div style={{ ...sectionStyle, borderLeftColor: '#22c55e' }}>
        <h3 style={questionStyle}>Footwork Comparison: 1-2 Step vs Hop — Which Has Better Success?</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div style={{ ...cardStyle, borderLeft: '3px solid #60a5fa' }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#60a5fa', marginBottom: 8 }}>1-2 Step (L-R / R-L)</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div>
                <div style={{ fontSize: 11, color: '#888' }}>Pre-Xmas</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: '#fff' }}>{fwFG(footworkStats['1-2_Step'].A)}%</div>
                <div style={{ fontSize: 11, color: '#888' }}>{footworkStats['1-2_Step'].A.made}/{footworkStats['1-2_Step'].A.total} shots</div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: '#888' }}>Post-Xmas</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: '#fff' }}>{fwFG(footworkStats['1-2_Step'].B)}%</div>
                <div style={{ fontSize: 11, color: '#888' }}>{footworkStats['1-2_Step'].B.made}/{footworkStats['1-2_Step'].B.total} shots</div>
              </div>
            </div>
          </div>
          <div style={{ ...cardStyle, borderLeft: '3px solid #f59e0b' }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#f59e0b', marginBottom: 8 }}>Two-Foot Hop</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div>
                <div style={{ fontSize: 11, color: '#888' }}>Pre-Xmas</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: '#fff' }}>{fwFG(footworkStats['Two_Foot_Hop'].A)}%</div>
                <div style={{ fontSize: 11, color: '#888' }}>{footworkStats['Two_Foot_Hop'].A.made}/{footworkStats['Two_Foot_Hop'].A.total} shots</div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: '#888' }}>Post-Xmas</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: '#fff' }}>{fwFG(footworkStats['Two_Foot_Hop'].B)}%</div>
                <div style={{ fontSize: 11, color: '#888' }}>{footworkStats['Two_Foot_Hop'].B.made}/{footworkStats['Two_Foot_Hop'].B.total} shots</div>
              </div>
            </div>
          </div>
        </div>
        {/* Overall winner callout */}
        {(() => {
          const step12All = footworkStats['1-2_Step'].A.total + footworkStats['1-2_Step'].B.total;
          const step12Made = footworkStats['1-2_Step'].A.made + footworkStats['1-2_Step'].B.made;
          const hopAll = footworkStats['Two_Foot_Hop'].A.total + footworkStats['Two_Foot_Hop'].B.total;
          const hopMade = footworkStats['Two_Foot_Hop'].A.made + footworkStats['Two_Foot_Hop'].B.made;
          const step12Pct = step12All > 0 ? (step12Made / step12All * 100).toFixed(1) : 0;
          const hopPct = hopAll > 0 ? (hopMade / hopAll * 100).toFixed(1) : 0;
          const winner = parseFloat(step12Pct) >= parseFloat(hopPct) ? '1-2 Step' : 'Two-Foot Hop';
          return (
            <div style={{ marginTop: 12, padding: 12, background: 'rgba(34, 197, 94, 0.08)', borderRadius: 8, textAlign: 'center' }}>
              <span style={{ fontSize: 13, color: '#22c55e', fontWeight: 700 }}>
                Overall: 1-2 Step {step12Pct}% ({step12Made}/{step12All}) vs Hop {hopPct}% ({hopMade}/{hopAll}) — {winner} wins
              </span>
            </div>
          );
        })()}
      </div>
    </div>
  );
}

// ─── Claude Chat Interface ──────────────────────────────────────────────────

function GeminiChatPanel() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [streamingText, setStreamingText] = useState('');
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => { scrollToBottom(); }, [messages, streamingText]);

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    const userMsg = { role: 'user', content: userMessage };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setIsLoading(true);
    setStreamingText('');

    try {
      const response = await fetch(`${API_BASE}/api/cs3s-chat/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userMessage,
          history: messages.slice(-10),
        }),
      });

      if (!response.ok) throw new Error(`Error: ${response.status}`);

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let fullText = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const event = JSON.parse(line.slice(6));
            if (event.type === 'content') {
              fullText += event.text;
              setStreamingText(fullText);
            } else if (event.type === 'error') {
              throw new Error(event.message);
            }
          } catch (e) {
            if (e.message !== 'error') { /* skip parse errors */ }
          }
        }
      }

      setMessages(prev => [...prev, { role: 'assistant', content: fullText }]);
      setStreamingText('');
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', content: `Error: ${err.message}` }]);
      setStreamingText('');
    } finally {
      setIsLoading(false);
    }
  };

  const suggestedQuestions = [
    'What are the biggest biomechanical differences between pre and post Christmas?',
    'Which players had the biggest shooting regression and why?',
    'Is 1-2 step or hop footwork more effective for this team?',
    'What does the C2R data tell us about cognitive fatigue?',
    'Which players should we focus on in practice to fix their mechanics?',
  ];

  const renderMarkdown = (text) => {
    try { return DOMPurify.sanitize(marked.parse(text)); } catch { return text; }
  };

  const chatBubbleStyle = (role) => ({
    maxWidth: '85%',
    padding: '10px 14px',
    borderRadius: 12,
    fontSize: 13,
    lineHeight: 1.7,
    wordBreak: 'break-word',
    ...(role === 'user'
      ? { background: 'rgba(99, 102, 241, 0.2)', color: '#c7d2fe', alignSelf: 'flex-end', borderBottomRightRadius: 4, whiteSpace: 'pre-wrap' }
      : { background: 'rgba(255,255,255,0.05)', color: '#d1d5db', alignSelf: 'flex-start', borderBottomLeftRadius: 4 }
    ),
  });

  return (
    <div style={{ ...cardStyle, marginTop: 16, display: 'flex', flexDirection: 'column', height: 600 }}>
      <style>{`
        .cs3s-chat-md h1, .cs3s-chat-md h2, .cs3s-chat-md h3, .cs3s-chat-md h4 {
          margin: 8px 0 4px 0; color: #e2e8f0; font-size: 14px;
        }
        .cs3s-chat-md h1 { font-size: 16px; }
        .cs3s-chat-md h2 { font-size: 15px; }
        .cs3s-chat-md h3 { font-size: 14px; }
        .cs3s-chat-md p { margin: 4px 0; }
        .cs3s-chat-md ul, .cs3s-chat-md ol { margin: 4px 0; padding-left: 20px; }
        .cs3s-chat-md li { margin: 2px 0; }
        .cs3s-chat-md strong { color: #c7d2fe; }
        .cs3s-chat-md code { background: rgba(99,102,241,0.15); padding: 1px 4px; border-radius: 3px; font-size: 12px; }
        .cs3s-chat-md pre { background: rgba(0,0,0,0.3); padding: 8px; border-radius: 6px; overflow-x: auto; margin: 6px 0; }
        .cs3s-chat-md pre code { background: none; padding: 0; }
        .cs3s-chat-md table { border-collapse: collapse; width: 100%; margin: 6px 0; font-size: 12px; }
        .cs3s-chat-md th, .cs3s-chat-md td { border: 1px solid rgba(255,255,255,0.12); padding: 4px 8px; text-align: left; }
        .cs3s-chat-md th { background: rgba(99,102,241,0.15); color: #c7d2fe; font-weight: 600; }
        .cs3s-chat-md hr { border: none; border-top: 1px solid rgba(255,255,255,0.1); margin: 8px 0; }
        .cs3s-chat-md blockquote { border-left: 3px solid #6366f1; margin: 6px 0; padding: 4px 12px; color: #9ca3af; }
        .cs3s-chat-md a { color: #818cf8; text-decoration: none; }
      `}</style>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 16, color: '#fff' }}>Ask SLAM AI — Biomechanical Analysis</h3>
        <span style={badgeStyle('#a78bfa')}>Claude Opus 4.6</span>
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8, paddingRight: 4 }}>
        {messages.length === 0 && !streamingText && (
          <div style={{ textAlign: 'center', color: '#888', marginTop: 40 }}>
            <p style={{ fontSize: 14, marginBottom: 16 }}>Ask about the Spurs CS3s biomechanical analysis.</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center' }}>
              {suggestedQuestions.map((q, i) => (
                <button
                  key={i}
                  onClick={() => { setInput(q); }}
                  style={{
                    background: 'rgba(99, 102, 241, 0.1)', border: '1px solid rgba(99, 102, 241, 0.3)',
                    color: '#a5b4fc', borderRadius: 8, padding: '6px 12px', fontSize: 12, cursor: 'pointer',
                    textAlign: 'left', maxWidth: 280,
                  }}
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((msg, i) => (
          <div key={i} style={chatBubbleStyle(msg.role)}>
            {msg.role === 'user' ? msg.content : (
              <div className="cs3s-chat-md" dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.content) }} />
            )}
          </div>
        ))}
        {streamingText && (
          <div style={chatBubbleStyle('assistant')}>
            <div className="cs3s-chat-md" dangerouslySetInnerHTML={{ __html: renderMarkdown(streamingText) }} />
            <span style={{ animation: 'blink 1s infinite', color: '#6366f1' }}>|</span>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <input
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMessage()}
          placeholder="Ask about biomechanics, footwork, player regression..."
          disabled={isLoading}
          style={{
            flex: 1, padding: '10px 14px', borderRadius: 10,
            background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)',
            color: '#fff', fontSize: 13, outline: 'none',
          }}
        />
        <button
          onClick={sendMessage}
          disabled={isLoading || !input.trim()}
          style={{
            padding: '10px 20px', borderRadius: 10, border: 'none',
            background: isLoading ? '#333' : '#6366f1', color: '#fff',
            fontSize: 13, fontWeight: 600, cursor: isLoading ? 'not-allowed' : 'pointer',
          }}
        >
          {isLoading ? 'Thinking...' : 'Send'}
        </button>
      </div>
    </div>
  );
}

// ─── CS3 Cognitive Scoring Panel ─────────────────────────────────────────────

const PERIOD_LABELS = {
  'CS3s 12.3.25 - 12.17.25': { label: 'Pre-Christmas', short: 'Pre-Xmas', color: '#60a5fa', dates: 'Dec 3-17' },
  'CS3s 12.18.25-12.25.25': { label: 'Christmas Week', short: 'Xmas Week', color: '#fbbf24', dates: 'Dec 18-25' },
  'CS3s 12.27.26 - 1.2.26': { label: 'Post-Christmas', short: 'Post-Xmas', color: '#f87171', dates: 'Dec 27-Jan 2' },
};

function CS3CognitivePanel({ cognitiveData }) {
  const [selectedPlayer, setSelectedPlayer] = useState(null);

  if (!cognitiveData || cognitiveData.length === 0) {
    return (
      <div style={{ ...cardStyle, marginTop: 16, textAlign: 'center', color: '#888' }}>
        <p>CS3 Cognitive data not available. Run the cognitive pipeline to generate scores.</p>
      </div>
    );
  }

  // Build cross-period player map
  const playerMap = {};
  cognitiveData.forEach(period => {
    const meta = PERIOD_LABELS[period.period] || { label: period.period, short: period.period, color: '#888', dates: '' };
    Object.values(period.player_summaries || {}).forEach(pl => {
      if (!playerMap[pl.shooter]) playerMap[pl.shooter] = {};
      playerMap[pl.shooter][period.period] = { ...pl, ...meta };
    });
  });

  // Sort players by average CS3 across periods (descending), filter out 0.0 scores
  const playerNames = Object.keys(playerMap).sort((a, b) => {
    const avgA = Object.values(playerMap[a]).filter(p => p.cs3_mean > 0).reduce((s, p) => s + p.cs3_mean, 0) / (Object.values(playerMap[a]).filter(p => p.cs3_mean > 0).length || 1);
    const avgB = Object.values(playerMap[b]).filter(p => p.cs3_mean > 0).reduce((s, p) => s + p.cs3_mean, 0) / (Object.values(playerMap[b]).filter(p => p.cs3_mean > 0).length || 1);
    return avgB - avgA;
  });

  const periods = cognitiveData.map(p => p.period);

  // CS3 score color
  const cs3Color = (score) => {
    if (score >= 7.7) return '#22c55e';
    if (score >= 7.5) return '#fbbf24';
    if (score > 0) return '#f87171';
    return '#555';
  };

  return (
    <div style={{ marginTop: 16 }}>
      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 16 }}>
        {cognitiveData.map(period => {
          const meta = PERIOD_LABELS[period.period] || { label: period.period, color: '#888', dates: '' };
          const players = Object.values(period.player_summaries || {}).filter(p => p.cs3_mean > 0);
          const avgCS3 = players.length > 0 ? (players.reduce((s, p) => s + p.cs3_mean, 0) / players.length).toFixed(2) : 'N/A';
          return (
            <div key={period.period} style={statBoxStyle(meta.color)}>
              <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>{meta.label}</div>
              <div style={{ fontSize: 11, color: '#666', marginBottom: 4 }}>{meta.dates}</div>
              <div style={{ fontSize: 24, fontWeight: 800, color: meta.color }}>{avgCS3}</div>
              <div style={{ fontSize: 11, color: '#888' }}>Team CS3 Mean</div>
              <div style={{ fontSize: 11, color: '#aaa', marginTop: 4 }}>
                {period.total_clips} clips | {period.phase_distribution?.phase_3_set_point || 0} set points
              </div>
            </div>
          );
        })}
      </div>

      {/* Cross-Period Comparison Table */}
      <div style={cardStyle}>
        <h3 style={{ margin: '0 0 12px', fontSize: 16, color: '#fff' }}>Player CS3 Cognitive Scores by Period</h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid rgba(255,255,255,0.15)' }}>
                <th style={{ textAlign: 'left', padding: '8px 12px', color: '#aaa', fontWeight: 600 }}>Player</th>
                {periods.map(p => {
                  const meta = PERIOD_LABELS[p] || { short: p, color: '#888' };
                  return (
                    <th key={p} style={{ textAlign: 'center', padding: '8px 12px', color: meta.color, fontWeight: 600 }}>
                      {meta.short}<br /><span style={{ fontSize: 10, color: '#666' }}>CS3</span>
                    </th>
                  );
                })}
                <th style={{ textAlign: 'center', padding: '8px 12px', color: '#a78bfa', fontWeight: 600 }}>
                  Trend
                </th>
                <th style={{ textAlign: 'center', padding: '8px 12px', color: '#aaa', fontWeight: 600 }}>
                  Clips
                </th>
              </tr>
            </thead>
            <tbody>
              {playerNames.map(name => {
                const pData = playerMap[name];
                const scores = periods.map(p => pData[p]?.cs3_mean || 0);
                const validScores = scores.filter(s => s > 0);
                const totalClips = periods.reduce((s, p) => s + (pData[p]?.clips_analyzed || 0), 0);

                // Trend: compare first and last valid scores
                let trend = '—';
                let trendColor = '#888';
                if (validScores.length >= 2) {
                  const first = validScores[0];
                  const last = validScores[validScores.length - 1];
                  const diff = last - first;
                  if (diff > 0.05) { trend = `+${diff.toFixed(2)}`; trendColor = '#22c55e'; }
                  else if (diff < -0.05) { trend = diff.toFixed(2); trendColor = '#f87171'; }
                  else { trend = '~0.00'; trendColor = '#fbbf24'; }
                }

                return (
                  <tr
                    key={name}
                    onClick={() => setSelectedPlayer(selectedPlayer === name ? null : name)}
                    style={{
                      borderBottom: '1px solid rgba(255,255,255,0.06)',
                      cursor: 'pointer',
                      background: selectedPlayer === name ? 'rgba(99,102,241,0.1)' : 'transparent',
                    }}
                  >
                    <td style={{ padding: '8px 12px', fontWeight: 600, color: '#fff' }}>{name}</td>
                    {periods.map(p => {
                      const score = pData[p]?.cs3_mean || 0;
                      return (
                        <td key={p} style={{ textAlign: 'center', padding: '8px 12px' }}>
                          <span style={{ color: cs3Color(score), fontWeight: 700, fontSize: 15 }}>
                            {score > 0 ? score.toFixed(2) : '—'}
                          </span>
                        </td>
                      );
                    })}
                    <td style={{ textAlign: 'center', padding: '8px 12px', color: trendColor, fontWeight: 600 }}>
                      {trend}
                    </td>
                    <td style={{ textAlign: 'center', padding: '8px 12px', color: '#aaa' }}>
                      {totalClips}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Selected Player Detail */}
      {selectedPlayer && playerMap[selectedPlayer] && (
        <div style={{ ...cardStyle, marginTop: 12, borderLeft: '3px solid #6366f1' }}>
          <h4 style={{ margin: '0 0 12px', fontSize: 15, color: '#fff' }}>{selectedPlayer} — Period Breakdown</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
            {periods.map(p => {
              const d = playerMap[selectedPlayer][p];
              if (!d) return null;
              const meta = PERIOD_LABELS[p] || { label: p, color: '#888' };
              return (
                <div key={p} style={{ ...cardStyle, borderTop: `2px solid ${meta.color}` }}>
                  <div style={{ fontSize: 12, color: meta.color, fontWeight: 600, marginBottom: 8 }}>{meta.label}</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, fontSize: 12 }}>
                    <div style={{ color: '#888' }}>CS3 Mean:</div>
                    <div style={{ color: cs3Color(d.cs3_mean), fontWeight: 700 }}>{d.cs3_mean > 0 ? d.cs3_mean.toFixed(2) : 'N/A'}</div>
                    <div style={{ color: '#888' }}>CS3 Peak:</div>
                    <div style={{ color: '#fff' }}>{d.cs3_peak > 0 ? d.cs3_peak.toFixed(2) : 'N/A'}</div>
                    <div style={{ color: '#888' }}>Confidence:</div>
                    <div style={{ color: '#fff' }}>{(d.confidence_mean * 100).toFixed(0)}%</div>
                    <div style={{ color: '#888' }}>Set Points:</div>
                    <div style={{ color: '#fff' }}>{d.set_point_count}</div>
                    <div style={{ color: '#888' }}>Clips:</div>
                    <div style={{ color: '#fff' }}>{d.clips_analyzed}</div>
                    <div style={{ color: '#888' }}>Frames OK:</div>
                    <div style={{ color: '#fff' }}>{d.frames_ok}/{d.total_frames}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Methodology Note */}
      <div style={{ ...cardStyle, marginTop: 12, borderLeft: '3px solid #a78bfa' }}>
        <h4 style={{ margin: '0 0 8px', fontSize: 13, color: '#a78bfa' }}>CS3 Scoring Methodology</h4>
        <p style={{ margin: 0, fontSize: 12, color: '#888', lineHeight: 1.6 }}>
          CS3 = Cognitive State at Set Point (Phase 3), scored 0-10. Computed by SLAM Cognitive ADK via Gemini 3 Flash
          vision analysis on each extracted frame. Vision-only mode maps phase detection confidence to score range [4, 8].
          Higher CS3 = cleaner, more readable shooting form at the critical decision point.
          Total: 2,050 frames processed across 235 clips with 146 set points detected.
        </p>
      </div>
    </div>
  );
}

// ─── Video Ingest Panel ─────────────────────────────────────────────────────

function VideoIngestPanel() {
  const [url, setUrl] = useState('');
  const [label, setLabel] = useState('');
  const [period, setPeriod] = useState('');
  const [coachName, setCoachName] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [activeJobId, setActiveJobId] = useState(null);
  const [activeJobStatus, setActiveJobStatus] = useState(null);
  const pollRef = useRef(null);

  // Load recent jobs on mount
  useEffect(() => {
    fetch(`${API_BASE}/api/video-ingest/jobs`)
      .then(r => r.json())
      .then(data => { setJobs(data.jobs || []); setLoadingJobs(false); })
      .catch(() => setLoadingJobs(false));
  }, []);

  // Poll active job status
  useEffect(() => {
    if (!activeJobId) return;
    const poll = () => {
      fetch(`${API_BASE}/api/video-ingest/status/${activeJobId}`)
        .then(r => r.json())
        .then(data => {
          setActiveJobStatus(data);
          if (data.status === 'complete' || data.status === 'error') {
            clearInterval(pollRef.current);
            // Refresh job list
            fetch(`${API_BASE}/api/video-ingest/jobs`)
              .then(r => r.json())
              .then(d => setJobs(d.jobs || []));
          }
        })
        .catch(() => {});
    };
    poll();
    pollRef.current = setInterval(poll, 3000);
    return () => clearInterval(pollRef.current);
  }, [activeJobId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitResult(null);
    try {
      const res = await fetch(`${API_BASE}/api/video-ingest/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url, label, period, coachName, notes }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Submit failed');
      setSubmitResult({ success: true, jobId: data.jobId });
      setActiveJobId(data.jobId);
      setUrl('');
      setLabel('');
      setPeriod('');
      setCoachName('');
      setNotes('');
    } catch (err) {
      setSubmitResult({ success: false, error: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '10px 14px',
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: 8,
    color: '#fff',
    fontSize: 14,
    outline: 'none',
    boxSizing: 'border-box',
  };

  const labelStyle = { display: 'block', fontSize: 12, color: '#aaa', marginBottom: 4, fontWeight: 600 };

  const statusColor = (status) => {
    if (status === 'complete') return '#22c55e';
    if (status === 'error') return '#ef4444';
    if (status === 'downloading' || status === 'resolving') return '#f59e0b';
    if (status === 'uploaded' || status === 'pipeline_queued') return '#60a5fa';
    return '#888';
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Submit Form */}
      <div style={cardStyle}>
        <h3 style={{ margin: '0 0 4px', fontSize: 16, color: '#fff' }}>Drop WeTransfer Link</h3>
        <p style={{ margin: '0 0 16px', fontSize: 13, color: '#888' }}>
          Paste a WeTransfer link below to upload game film for CS3 analysis. Video will be streamed directly to cloud storage.
        </p>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label style={labelStyle}>WeTransfer URL *</label>
            <input
              type="url"
              value={url}
              onChange={e => setUrl(e.target.value)}
              placeholder="https://we.tl/... or https://wetransfer.com/downloads/..."
              required
              style={inputStyle}
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle}>Label / Game</label>
              <input
                value={label}
                onChange={e => setLabel(e.target.value)}
                placeholder="e.g. Spurs vs OKC 1/15"
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>Period / Dataset</label>
              <input
                value={period}
                onChange={e => setPeriod(e.target.value)}
                placeholder="e.g. Post-Christmas"
                style={inputStyle}
              />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle}>Coach Name</label>
              <input
                value={coachName}
                onChange={e => setCoachName(e.target.value)}
                placeholder="e.g. Coach Baron"
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>Notes</label>
              <input
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Optional notes"
                style={inputStyle}
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={submitting || !url}
            style={{
              padding: '12px 24px',
              background: submitting ? '#333' : 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              border: 'none',
              borderRadius: 8,
              color: '#fff',
              fontWeight: 700,
              fontSize: 14,
              cursor: submitting ? 'not-allowed' : 'pointer',
              opacity: (!url || submitting) ? 0.5 : 1,
              transition: 'all 0.2s',
            }}
          >
            {submitting ? 'Submitting...' : 'Start Video Ingestion'}
          </button>
        </form>

        {submitResult && (
          <div style={{
            marginTop: 12,
            padding: '10px 14px',
            borderRadius: 8,
            background: submitResult.success ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)',
            border: `1px solid ${submitResult.success ? '#22c55e44' : '#ef444444'}`,
            fontSize: 13,
            color: submitResult.success ? '#22c55e' : '#ef4444',
          }}>
            {submitResult.success
              ? `Ingestion started. Job ID: ${submitResult.jobId}`
              : `Error: ${submitResult.error}`}
          </div>
        )}
      </div>

      {/* Active Job Progress — Agent Trace Panel */}
      {activeJobStatus && (
        <AgentTracePanel
          status={activeJobStatus.status}
          progress={activeJobStatus.progress || 0}
          message={activeJobStatus.message}
          agentTrace={activeJobStatus.agentTrace || []}
          gcsUri={activeJobStatus.gcsUri}
        />
      )}

      {/* Email Ingest Info */}
      <div style={{ ...cardStyle, borderLeft: '3px solid #8b5cf6' }}>
        <h3 style={{ margin: '0 0 8px', fontSize: 14, color: '#fff' }}>Email Ingestion</h3>
        <p style={{ margin: 0, fontSize: 13, color: '#aaa' }}>
          Coaches can also email WeTransfer links to{' '}
          <strong style={{ color: '#8b5cf6' }}>ingest@slam.coach</strong>{' '}
          and the pipeline will automatically process the video. Include the game name in the subject line.
        </p>
      </div>

      {/* Recent Jobs */}
      <div style={cardStyle}>
        <h3 style={{ margin: '0 0 12px', fontSize: 16, color: '#fff' }}>Recent Ingestion Jobs</h3>
        {loadingJobs ? (
          <p style={{ color: '#888', fontSize: 13 }}>Loading jobs...</p>
        ) : jobs.length === 0 ? (
          <p style={{ color: '#888', fontSize: 13 }}>No ingestion jobs yet. Submit a WeTransfer link above to get started.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                  <th style={{ textAlign: 'left', padding: '8px 12px', color: '#888', fontWeight: 600 }}>Status</th>
                  <th style={{ textAlign: 'left', padding: '8px 12px', color: '#888', fontWeight: 600 }}>Label</th>
                  <th style={{ textAlign: 'left', padding: '8px 12px', color: '#888', fontWeight: 600 }}>Source</th>
                  <th style={{ textAlign: 'left', padding: '8px 12px', color: '#888', fontWeight: 600 }}>Progress</th>
                  <th style={{ textAlign: 'left', padding: '8px 12px', color: '#888', fontWeight: 600 }}>Created</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map(job => (
                  <tr key={job.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                    <td style={{ padding: '8px 12px' }}>
                      <span style={badgeStyle(statusColor(job.status))}>{job.status}</span>
                    </td>
                    <td style={{ padding: '8px 12px', color: '#ddd' }}>{job.label || '—'}</td>
                    <td style={{ padding: '8px 12px', color: '#aaa' }}>{job.source || '—'}</td>
                    <td style={{ padding: '8px 12px' }}>
                      <div style={{ width: 80, height: 6, background: 'rgba(255,255,255,0.06)', borderRadius: 3 }}>
                        <div style={{
                          width: `${job.progress || 0}%`,
                          height: '100%',
                          background: statusColor(job.status),
                          borderRadius: 3,
                        }} />
                      </div>
                    </td>
                    <td style={{ padding: '8px 12px', color: '#888', fontSize: 12 }}>
                      {new Date(job.createdAt).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main Dashboard ──────────────────────────────────────────────────────────

export default function CS3sBiomechDashboard({ defaultTab }) {
  const [activeTab, setActiveTab] = useState(defaultTab || 'coaching');
  const [synthesis, setSynthesis] = useState(null);
  const [batchFiles, setBatchFiles] = useState([]);
  const [allShots, setAllShots] = useState([]);
  const [cognitiveData, setCognitiveData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);
  const synthesisRef = useRef(null);

  const fetchData = useCallback(async () => {
    // If synthesis already loaded, stop polling — analysis is done
    if (synthesisRef.current) {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return;
    }

    try {
      // Parallel fetch: synthesis, cognitive data, and batch file list
      const [synthResult, cogResult, listResult] = await Promise.allSettled([
        fetch(`${API_BASE}/api/gcs-proxy/${GCS_PREFIX}/synthesis.json`).then(r => r.ok ? r.json() : null),
        fetch(`${API_BASE}/api/cs3s-chat/cognitive`).then(r => r.ok ? r.json() : null),
        fetch(`${API_BASE}/api/gcs-list?prefix=${encodeURIComponent(`${GCS_PREFIX}/batches/`)}`).then(r => r.ok ? r.json() : null),
      ]);

      if (synthResult.status === 'fulfilled' && synthResult.value) {
        setSynthesis(synthResult.value);
        synthesisRef.current = synthResult.value;
        // Synthesis loaded — stop polling
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      }
      if (cogResult.status === 'fulfilled' && cogResult.value) {
        setCognitiveData(cogResult.value);
      }

      const batchNames = (listResult.status === 'fulfilled' && listResult.value)
        ? (listResult.value.files || []).filter(f => f.endsWith('.json'))
        : [];

      // Fetch all batch files in parallel (not sequentially)
      if (batchNames.length > 0) {
        const batchResults = await Promise.allSettled(
          batchNames.map(fullPath =>
            fetch(`${API_BASE}/api/gcs-proxy/${fullPath}`).then(r => r.ok ? r.json() : null)
          )
        );
        const shotData = [];
        for (const result of batchResults) {
          if (result.status === 'fulfilled' && Array.isArray(result.value)) {
            shotData.push(...result.value);
          }
        }
        setBatchFiles(batchNames);
        setAllShots(shotData);
      }

      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    // Poll every 30s while analysis is running (stopped once synthesis loads)
    pollRef.current = setInterval(fetchData, 30000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#fff' }}>
              Spurs Shooting Analysis
            </h1>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: '#888' }}>
              San Antonio Spurs 2025-26 | CS3s + Free Throws | Gemini 3.1 Pro Preview
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <span style={badgeStyle('#60a5fa')}>Dataset A: 36.7% 3PT</span>
            <span style={badgeStyle('#f87171')}>Dataset B: 32.3% 3PT</span>
            <span style={badgeStyle('#f59e0b')}>-4.4% Drop</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: '20px 32px' }}>
        {/* Progress */}
        <ProgressPanel
          batchFiles={batchFiles}
          totalExpected={TOTAL_EXPECTED_BATCHES}
          synthesis={synthesis}
        />

        {/* Baseline Stats */}
        <BaselineComparison />

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 4, marginTop: 20, borderBottom: '1px solid rgba(255,255,255,0.08)', overflowX: 'auto' }}>
          {[
            { id: 'coaching', label: 'Coaching Q&A' },
            { id: 'chat', label: 'Ask AI' },
            { id: 'cognitive', label: 'CS3 Cognitive' },
            { id: 'overview', label: 'Overview' },
            { id: 'players', label: 'Player Matrix' },
            { id: 'profiles', label: 'Shot Profiles' },
            { id: 'shots', label: 'Shot Browser' },
            { id: 'evidence', label: 'Evidence Chain' },
            { id: 'free-throws', label: 'Free Throws' },
            { id: 'ingest', label: 'Video Ingest' },
          ].map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)} style={tabStyle(activeTab === t.id)}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div style={{ marginTop: 4 }}>
          {activeTab === 'coaching' && (
            <CoachingQA shots={allShots} synthesis={synthesis} />
          )}
          {activeTab === 'chat' && (
            <GeminiChatPanel />
          )}
          {activeTab === 'cognitive' && (
            <CS3CognitivePanel cognitiveData={cognitiveData} />
          )}
          {activeTab === 'overview' && (
            <>
              <MacroDiagnosis synthesis={synthesis} />
              {synthesis ? (
                <DatasetComparisonPanel synthesis={synthesis} />
              ) : (
                <AggregateStats shots={allShots} />
              )}
              {!synthesis && allShots.length > 0 && (
                <div style={{ ...cardStyle, marginTop: 16, textAlign: 'center', color: '#888' }}>
                  <p>Synthesis not yet complete. Showing computed aggregates from {allShots.length} shots analyzed so far.</p>
                  <p style={{ fontSize: 12 }}>Full Player Matrix and Evidence Chain will appear when analysis completes.</p>
                </div>
              )}
              {loading && allShots.length === 0 && (
                <div style={{ ...cardStyle, marginTop: 16, textAlign: 'center', color: '#888' }}>
                  <p>Loading analysis data...</p>
                </div>
              )}
            </>
          )}
          {activeTab === 'players' && (
            synthesis ? <PlayerMatrix synthesis={synthesis} /> : (
              <div style={{ ...cardStyle, marginTop: 16, textAlign: 'center', color: '#888' }}>
                Player Matrix will be available after synthesis completes.
              </div>
            )
          )}
          {activeTab === 'profiles' && <PlayerProfileCards shots={allShots} />}
          {activeTab === 'shots' && <ShotBrowser shots={allShots} />}
          {activeTab === 'evidence' && (
            synthesis ? <EvidenceChain synthesis={synthesis} /> : (
              <div style={{ ...cardStyle, marginTop: 16, textAlign: 'center', color: '#888' }}>
                Evidence Chain will be available after synthesis completes.
              </div>
            )
          )}
          {activeTab === 'free-throws' && <FreeThrowTab />}
          {activeTab === 'ingest' && <VideoIngestPanel />}
        </div>

        {error && (
          <div style={{ ...cardStyle, marginTop: 16, borderLeft: '3px solid #ef4444', color: '#f87171' }}>
            Error: {error}
          </div>
        )}
      </div>
    </div>
  );
}
